import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Tooltip } from 'antd';
import CustomModal from '../../components/common/CustomModal';
import { FiHelpCircle } from 'react-icons/fi';
import methodModel from '../../methods/methods';
import Layout from '../../components/global/layout';
import moment from 'moment';
import ApiClient from '../../methods/api/apiClient';
import loader from '../../methods/loader';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import './Analytics.css';

// Modular Subcomponents
import AnalyticsHeader from './components/AnalyticsHeader';
import StatCardsGrid from './components/StatCardsGrid';
import ActiveUsersChart from './components/ActiveUsersChart';
import PlatformBreakdownChart from './components/PlatformBreakdownChart';
import UserRetentionCard from './components/UserRetentionCard';
import VenueAnalyticsTable from './components/VenueAnalyticsTable';
import DailySignupChart from './components/DailySignupChart';

function AnalyticsDashboard() {
  const user = useSelector((state) => state.user);
  const navigate = useNavigate();

  // Redirect if user does not have read permission for Analytics
  useEffect(() => {
    if (user && user.loggedIn && !methodModel.hasPermission(user, 'Analytics', 'read')) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Date Filter State
  const [dateRange, setDateRange] = useState({
    startDate: moment().subtract(30, 'days').toDate(),
    endDate: moment().toDate(),
    key: 'selection',
  });

  const [activeFilterTab, setActiveFilterTab] = useState('30d');
  const [isLoading, setIsLoading] = useState(false);
  const [isExportingPDF, setIsExportingPDF] = useState(false);
  const [chartType, setChartType] = useState('line');
  const [venueSearch, setVenueSearch] = useState('');
  const [venueFilter, setVenueFilter] = useState('all');
  const [sortField, setSortField] = useState('totalUsers');
  const [sortOrder, setSortOrder] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [isHelpModalVisible, setIsHelpModalVisible] = useState(false);

  // Preset Date Handlers
  const handleQuickDateChange = useCallback((preset) => {
    setActiveFilterTab(preset);
    let start = moment();
    const end = moment().toDate();

    if (preset === 'today') {
      start = moment().startOf('day');
    } else if (preset === '7d') {
      start = moment().subtract(7, 'days');
    } else if (preset === '30d') {
      start = moment().subtract(30, 'days');
    } else if (preset === '90d') {
      start = moment().subtract(90, 'days');
    } else if (preset === 'ytd') {
      start = moment().startOf('year');
    }

    setDateRange({
      startDate: start.toDate(),
      endDate: end,
      key: 'selection',
    });
  }, []);

  // State data for Analytics
  const [analyticsData, setAnalyticsData] = useState(null);
  const [rawVenueAnalytics, setRawVenueAnalytics] = useState([]);
  const [totalVenues, setTotalVenues] = useState(0);
  const [allVenuesLookup, setAllVenuesLookup] = useState({});

  // Fetch or Calculate Analytics Data
  const fetchAnalyticsData = useCallback(() => {
    setIsLoading(true);

    const sDate = moment(dateRange.startDate).format('YYYY-MM-DD');
    const eDate = moment(dateRange.endDate).format('YYYY-MM-DD');
    const params = { startDate: sDate, endDate: eDate };

    Promise.all([
      ApiClient.get('dashboard/admin/analytics/overview', params),
      ApiClient.get('dashboard/admin/analytics/engagement', params),
      ApiClient.get('dashboard/admin'),
      ApiClient.get('dashboard/admin/analytics/retention', params),
    ])
      .then(([overviewRes, engagementRes, adminRes, retentionRes]) => {
        console.log({ overviewRes, engagementRes, adminRes, retentionRes }, "THIS IS RES-----")
        const overviewData = overviewRes?.data || {};
        const engagementData = engagementRes?.data || {};
        const adminData = adminRes?.data || {};
        const retentionData = retentionRes?.data?.retention || {};
        const peakHoursData = retentionRes?.data?.peakHours || {};

        const totalUsers = overviewData.totalUsers ?? 0;
        const webUsers = overviewData.webSignups ?? 0;
        const appUsers = overviewData.appSignups ?? 0;
        const appIosUsers = appUsers;
        const appAndroidUsers = appUsers;

        setAnalyticsData({
          totalUsers,
          webUsers,
          appUsers,
          appIosUsers,
          appAndroidUsers,
          totalAppTime: engagementData.totalAppTime ?? 0,
          totalVenueTime: engagementData.totalVenueTime ?? 0,
          totalAppTimeReadable: engagementData.totalAppTimeReadable ?? '0s',
          totalVenueTimeReadable: engagementData.totalVenueTimeReadable ?? '0s',
          avgAppTime: engagementData.avgAppTime ?? 0,
          avgVenueTime: engagementData.avgVenueTime ?? 0,
          avgAppTimeReadable: engagementData.avgAppTimeReadable ?? '0s',
          avgVenueTimeReadable: engagementData.avgVenueTimeReadable ?? '0s',
          bounceRate: engagementData.bounceRate ?? 0,
          totalClicks: engagementData.totalClicks ?? 0,
          totalVenueClicks: engagementData.totalVenueClicks ?? 0,
          totalSessions: engagementData.totalSessions ?? 0,
          uniqueUsers: engagementData.uniqueUsers ?? 0,
          avgClicksPerSession: engagementData.avgClicksPerSession ?? 0,
          activeVenuesCount: adminData.totalVenues,
          signupGraph: overviewData.signupGraph || [],
          dailyEngagement: engagementData.dailyEngagement || [],
          retention: {
            totalUniqueUsers: retentionData.totalUniqueUsers ?? 0,
            loyalUsers: retentionData.loyalUsers ?? 0,
            newUsers: retentionData.newUsers ?? 0,
            returningLoyalUsers: retentionData.returningLoyalUsers ?? 0,
            returningLoyalRate: retentionData.returningLoyalRate ?? 0,
            retentionGraph: retentionData.retentionGraph || [],
          },
          peakWindows: peakHoursData.peakWindows || [],
          topHours: peakHoursData.topHours || [],
        });

        setIsLoading(false);
      })
      .catch((err) => {
        setAllVenuesLookup({});
        setIsLoading(false);
      });
  }, [dateRange]);

  useEffect(() => {
    fetchAnalyticsData();
  }, [fetchAnalyticsData]);

  // Fetch Venue Analytics API and log response
  const fetchVenueAnalytics = useCallback(() => {
    setIsTableLoading(true);
    const sDate = moment(dateRange.startDate).format('YYYY-MM-DD');
    const eDate = moment(dateRange.endDate).format('YYYY-MM-DD');
    const params = {
      search: venueSearch,
      sortBy: `${sortField} ${sortOrder}`,
      page: currentPage,
      count: itemsPerPage,
      startDate: sDate,
      endDate: eDate,
      status: 'active'
    };

    ApiClient.get('dashboard/admin/analytics/venues', params)
      .then((res) => {
        console.log('Venue Analytics API Response:', res);
        if (res.success) {
          setRawVenueAnalytics(res.data || []);
          setTotalVenues(res.total || 0);
        } else {
          setRawVenueAnalytics([]);
          setTotalVenues(0);
        }
        setIsTableLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching venue analytics:', err);
        setRawVenueAnalytics([]);
        setTotalVenues(0);
        setIsTableLoading(false);
      });
  }, [venueSearch, sortField, sortOrder, currentPage, itemsPerPage, dateRange]);

  useEffect(() => {
    fetchVenueAnalytics();
  }, [fetchVenueAnalytics]);

  // Map raw venue analytics and look up locations
  const venuesList = useMemo(() => {
    return rawVenueAnalytics.map((v, index) => {
      return {
        id: v.venueId || index,
        name: v.name || 'Unnamed Venue',
        logo: v.logo || '',
        category: v.type || 'Other',
        location: allVenuesLookup[v.venueId] || '—',
        totalUsers: v.totalUsers || 0,
        totalAppTime: v.totalAppTime || 0,
        timeSpentHours: v?.totalAppTimeReadable || '0s',
        bounceRate: (v.bounceRate || 0) + '%',
        totalClicks: v.totalClicks || 0,
        status: v.venueStatus || 'Active',
        trend: '0%',
        users: Array.isArray(v.users) ? v.users : [],
      };
    });
  }, [rawVenueAnalytics, allVenuesLookup]);

  // Filtered & Sorted Venues (not sorting since API does filtering/sorting)
  const filteredVenues = useMemo(() => {
    let result = [...venuesList];
    if (venueFilter === 'top') {
      result = result.filter((v) => v.status === 'Top Performer' || v.status === 'High Activity' || v.status == 'Top Activity');
    } else if (venueFilter === 'trending') {
      result = result.filter((v) => v.status === 'Trending');
    }
    return result;
  }, [venuesList, venueFilter]);

  // Paginated Venues (already paginated by API)
  const paginatedVenues = filteredVenues;
  const totalPages = Math.ceil(totalVenues / itemsPerPage);

  // Generate chart labels based on Date Range
  const chartLabels = useMemo(() => {
    const days = Math.max(7, Math.min(30, moment(dateRange.endDate).diff(moment(dateRange.startDate), 'days')));
    const labels = [];
    for (let i = days; i >= 0; i--) {
      labels.push(moment().subtract(i, 'days').format('MMM DD'));
    }
    return labels;
  }, [dateRange]);

  // Active Users Graph Data (Total Active Users Only)
  const activeUsersGraphData = useMemo(() => {
    let labels = [];
    let data = [];
    let details = [];

    if (analyticsData?.dailyEngagement && analyticsData.dailyEngagement.length > 0) {
      labels = analyticsData.dailyEngagement.map(item => moment(item.date || item._id).format('MMM DD'));
      data = analyticsData.dailyEngagement.map(item => item.uniqueUserCount ?? item.uniqueUsers ?? item.activeUsers ?? 0);
      details = analyticsData.dailyEngagement;
    } else if (analyticsData?.signupGraph && analyticsData.signupGraph.length > 0) {
      labels = analyticsData.signupGraph.map(item => moment(item.date || item._id).format('MMM DD'));
      data = analyticsData.signupGraph.map(item => item.count || item.totalUsers || 0);
      details = analyticsData.signupGraph.map(item => ({ uniqueUserCount: item.count || item.totalUsers }));
    }

    return {
      labels,
      datasets: [
        {
          label: 'Total Active Users',
          data: data,
          dailyDetails: details,
          borderColor: '#CC9900',
          backgroundColor: (context) => {
            const ctx = context.chart.ctx;
            const gradient = ctx.createLinearGradient(0, 0, 0, 320);
            gradient.addColorStop(0, 'rgba(204, 153, 0, 0.4)');
            gradient.addColorStop(1, 'rgba(204, 153, 0, 0.01)');
            return gradient;
          },
          borderWidth: 3.5,
          tension: 0.4,
          fill: true,
          pointBackgroundColor: '#FFFFFF',
          pointBorderColor: '#CC9900',
          pointBorderWidth: 2.5,
          pointRadius: 4,
          pointHoverRadius: 8,
        },
      ],
    };
  }, [analyticsData]);

  // Venue Check-Ins vs Offers Claimed Graph Data
  const checkinsOffersGraphData = useMemo(() => {
    let labels = [];
    let checkinSeries = [];
    let offerSeries = [];

    if (analyticsData?.dailyEngagement && analyticsData.dailyEngagement.length > 0) {
      labels = analyticsData.dailyEngagement.map(item => moment(item.date || item._id).format('MMM DD'));
      checkinSeries = analyticsData.dailyEngagement.map(item => item.totalVenueClicks || 0);
      offerSeries = analyticsData.dailyEngagement.map(item => item.totalClicks || 0);
    }

    return {
      labels,
      datasets: [
        {
          type: 'bar',
          label: 'Venue Check-Ins',
          data: checkinSeries,
          backgroundColor: '#3B82F6',
          borderRadius: 6,
        },
        {
          type: 'line',
          label: 'Offers Claimed',
          data: offerSeries,
          borderColor: '#CC9900',
          backgroundColor: 'rgba(204, 153, 0, 0.15)',
          borderWidth: 3,
          tension: 0.3,
          pointBackgroundColor: '#CC9900',
          fill: true,
        },
      ],
    };
  }, [analyticsData]);

  // Platform Doughnut Data
  const platformDoughnutData = useMemo(() => {
    return {
      labels: ['Guest User', 'Mobile App'],
      datasets: [
        {
          data: [
            analyticsData?.webUsers || 0,
            analyticsData?.appIosUsers || 0,
          ],
          backgroundColor: ['#3B82F6', '#CC9900', '#10B981'],
          borderWidth: 4,
          borderColor: '#FFFFFF',
          hoverOffset: 6,
        },
      ],
    };
  }, [analyticsData]);

  // Daily Signups Graph Data
  const signupGraphData = useMemo(() => {
    let labels = [];
    let appSeries = [];
    let webSeries = [];
    let details = [];

    if (analyticsData?.signupGraph && analyticsData.signupGraph.length > 0) {
      labels = analyticsData.signupGraph.map(item => moment(item.date || item._id).format('MMM DD'));
      appSeries = analyticsData.signupGraph.map(item => item.app || 0);
      webSeries = analyticsData.signupGraph.map(item => item.web || 0);
      details = analyticsData.signupGraph;
    }

    return {
      labels,
      datasets: [
        {
          label: 'App Signups',
          data: appSeries,
          dailyDetails: details,
          backgroundColor: '#3B82F6',
          borderRadius: 4,
        },
        {
          label: 'Web Signups',
          data: webSeries,
          dailyDetails: details,
          backgroundColor: '#CC9900',
          borderRadius: 4,
        }
      ],
    };
  }, [analyticsData]);

  // Export CSV Handler
  const handleExportCSV = useCallback(() => {
    const headers = ['Venue Name', 'Category', 'Location', 'Total Users', 'Time Spent (Hours)', 'Bounce Rate', 'Total Clicks', 'Status'];
    const rows = filteredVenues.map((v) => [
      `"${v.name}"`,
      `"${v.category}"`,
      `"${v.location}"`,
      v.totalUsers,
      v.timeSpentHours,
      `"${v.bounceRate}"`,
      v.totalClicks,
      `"${v.status}"`,
    ]);

    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map((e) => e.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `Venue_Analytics_${moment().format('YYYY-MM-DD')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [filteredVenues]);

  // Multi-Page PDF Engine using dynamic vertical canvas slicing for 100% visual fidelity
  const handleExportPDF = useCallback(async () => {
    try {
      setIsExportingPDF(true);
      const container = document.getElementById('pdf-dashboard-container');
      if (!container) return;

      // Scroll to top to ensure complete capture
      window.scrollTo(0, 0);
      await new Promise((resolve) => setTimeout(resolve, 300));

      // Capture the entire dashboard container
      const canvas = await html2canvas(container, {
        scale: 2, // High DPI capture
        useCORS: true,
        logging: false,
        backgroundColor: '#F9FAFB',
        scrollX: 0,
        scrollY: 0,
      });

      // US Letter dimensions in mm: 215.9 × 279.4
      const pdf = new jsPDF('p', 'mm', 'letter');
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const contentW = pageW - margin * 2;
      const pageContentH = pageH - margin * 2;

      // Slice canvas vertically to fit on successive Letter pages
      const pageCanvas = document.createElement('canvas');
      const ctx = pageCanvas.getContext('2d');
      if (!ctx) throw new Error('Failed to get 2D context');

      pageCanvas.width = canvas.width;
      const pxPerPage = (pageContentH * canvas.width) / contentW;

      let sourceY = 0;
      let pageNum = 1;

      while (sourceY < canvas.height) {
        const sourceH = Math.min(pxPerPage, canvas.height - sourceY);

        pageCanvas.height = sourceH;
        ctx.clearRect(0, 0, pageCanvas.width, sourceH);
        ctx.drawImage(
          canvas,
          0, sourceY, canvas.width, sourceH,
          0, 0, pageCanvas.width, sourceH
        );

        if (pageNum > 1) {
          pdf.addPage();
        }

        const destH = (sourceH * contentW) / canvas.width;
        pdf.addImage(
          pageCanvas.toDataURL('image/png', 1.0),
          'PNG',
          margin,
          margin,
          contentW,
          destH,
          '',
          'FAST'
        );

        sourceY += sourceH;
        pageNum++;
      }

      pdf.save(`TavLoy_Analytics_Dashboard_${moment().format('YYYY-MM-DD')}.pdf`);
    } catch (err) {
      console.error('PDF export error:', err);
    } finally {
      setIsExportingPDF(false);
    }
  }, []);

  return (
    <Layout>
      <div id="pdf-dashboard-container" className="space-y-6 pb-12 animate-scale-fade bg-[#F9FAFB] p-4 sm:p-6 rounded-[24px]">
        {/* ========================================================================= */}
        {/* PDF PAGE 1 SECTION: HEADER + STAT CARDS + ACTIVE USERS + PLATFORM DOUGHNUT */}
        {/* ========================================================================= */}
        <div id="pdf-section-1" className="space-y-6">
          {/* Top Header & Filters */}
          <AnalyticsHeader
            activeFilterTab={activeFilterTab}
            handleQuickDateChange={handleQuickDateChange}
            dateRange={dateRange}
            setDateRange={setDateRange}
            setActiveFilterTab={setActiveFilterTab}
            fetchAnalyticsData={fetchAnalyticsData}
            isLoading={isLoading}
            handleExportCSV={handleExportCSV}
            handleExportPDF={handleExportPDF}
            isExportingPDF={isExportingPDF}
            analyticsData={analyticsData}
          />

          {/* Must-Have Stat Cards */}
          <StatCardsGrid analyticsData={analyticsData} isLoading={isLoading} />

          {/* Active Users & Platform Distribution Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <ActiveUsersChart
                chartType={chartType}
                setChartType={setChartType}
                activeUsersGraphData={activeUsersGraphData}
                analyticsData={analyticsData}
                isLoading={isLoading}
              />
            </div>
            <div>
              <PlatformBreakdownChart
                platformDoughnutData={platformDoughnutData}
                analyticsData={analyticsData}
                isLoading={isLoading}
              />
            </div>
          </div>
        </div>

        {/* ========================================================================= */}
        {/* PDF PAGE 2 SECTION: CHECKINS CHART + USER RETENTION + VENUE TABLE */}
        {/* ========================================================================= */}
        <div id="pdf-section-2" className="space-y-6 pt-2">
          {/* Signups & User Retention Addons */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <DailySignupChart signupGraphData={signupGraphData} isLoading={isLoading} />
            </div>
            <div>
              <UserRetentionCard analyticsData={analyticsData} isLoading={isLoading} />
            </div>
          </div>

          {/* Venue Analytics Table */}
          <VenueAnalyticsTable
            venueFilter={venueFilter}
            setVenueFilter={setVenueFilter}
            venueSearch={venueSearch}
            setVenueSearch={setVenueSearch}
            sortField={sortField}
            setSortField={setSortField}
            sortOrder={sortOrder}
            setSortOrder={setSortOrder}
            paginatedVenues={paginatedVenues}
            filteredVenues={filteredVenues}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            totalPages={totalPages}
            itemsPerPage={itemsPerPage}
            totalVenuesCount={totalVenues}
            isTableLoading={isTableLoading}
          />
        </div>
      </div>

      {/* How It Works Guide Modal */}
      <CustomModal
        open={isHelpModalVisible}
        onClose={() => setIsHelpModalVisible(false)}
        width={650}
      >
        <div className="flex flex-col font-sans">
          {/* Header */}
          <div className="bg-gradient-to-r from-[#CC9900] via-[#E6B800] to-[#CC9900] p-6 text-white relative">
            <div className="flex justify-between items-start gap-4">
              <div>
                <h3 className="text-xl font-bold leading-snug">
                  💡 How the Analytics Dashboard Works
                </h3>
                <p className="text-sm text-white/90 mt-1">
                  Understanding business growth, user metrics, and venue engagement
                </p>
              </div>
            </div>
          </div>

          {/* Ticket Dotted Separator */}
          <div className="relative bg-gray-50" style={{ height: '28px' }}>
            <svg className="absolute left-0 top-0 h-full" style={{ width: '14px' }} viewBox="0 0 14 28" preserveAspectRatio="none">
              <path d="M0,0 C14,0 14,28 0,28 Z" fill="white" />
            </svg>
            <div className="absolute" style={{ top: '50%', left: '14px', right: '14px', transform: 'translateY(-50%)', borderTop: '1.5px dashed #d1d5db' }} />
            <svg className="absolute right-0 top-0 h-full" style={{ width: '14px' }} viewBox="0 0 14 28" preserveAspectRatio="none">
              <path d="M14,0 C0,0 0,28 14,28 Z" fill="white" />
            </svg>
          </div>

          {/* Modal Body */}
          <div className="p-6 space-y-6">
            <p className="text-sm text-gray-600 leading-relaxed">
              The Analytics dashboard provides real-time performance insights across the platform. You can monitor the following metrics:
            </p>

            <div className="space-y-4">
              {/* Overview Metrics */}
              <div className="flex gap-4 p-4 rounded-xl border border-[#CC9900]/20 bg-[#CC9900]/5">
                <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-[#CC9900]/10 text-[#CC9900] flex-shrink-0 font-bold text-sm">1</span>
                <div>
                  <h4 className="font-bold text-[#8B6E1E] text-sm mb-1">Key Performance Indicators (KPIs)</h4>
                  <p className="text-xs text-gray-600 leading-relaxed">
                    Overview counters show total user signups, engagement sessions, total and average app/venue interaction times, and click statistics.
                  </p>
                </div>
              </div>

              {/* Engagement Graphs */}
              <div className="flex gap-4 p-4 rounded-xl border border-gray-200 bg-gray-50">
                <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-gray-200/50 text-gray-500 flex-shrink-0 font-bold text-sm">2</span>
                <div>
                  <h4 className="font-bold text-gray-700 text-sm mb-1">Engagement & Signup Trends</h4>
                  <p className="text-xs text-gray-600 leading-relaxed">
                    Interactive line and bar charts display active customer session trends, registration growth, and platform-specific usage distributions over time.
                  </p>
                </div>
              </div>

              {/* Venue analytics */}
              <div className="flex gap-4 p-4 rounded-xl border border-amber-100 bg-amber-50/50">
                <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-amber-100 text-amber-600 font-bold text-sm flex-shrink-0">3</span>
                <div>
                  <h4 className="font-bold text-[#8B6E1E] text-sm mb-1">Venue Specific Analytics</h4>
                  <p className="text-xs text-gray-600 leading-relaxed">
                    The venue table tracks specific venue metrics including total users, bounce rate, click counts, and engagement duration to identify top-performing partners.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-100 flex justify-end">
            <button
              onClick={() => setIsHelpModalVisible(false)}
              className="px-5 py-2 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-100 transition-all text-xs font-semibold"
            >
              Got it, thanks!
            </button>
          </div>
        </div>
      </CustomModal>

      {/* Floating Help Button */}
      <Tooltip placement="left" title="How It Works">
        <button
          type="button"
          onClick={() => setIsHelpModalVisible(true)}
          className="fixed bottom-6 right-6 z-50 flex items-center justify-center w-12 h-12 rounded-full bg-[#CC9900] text-white shadow-lg hover:bg-[#B38000] hover:scale-110 active:scale-95 transition-all duration-300"
        >
          <FiHelpCircle className="text-2xl" />
        </button>
      </Tooltip>
    </Layout>
  );
}

export default AnalyticsDashboard;
